import { createContext } from "react";
import type {
  IntakeAnswers,
  Product,
  SetupProgress,
  TaskId,
} from "../registry/types";
import type {
  TaskCompletionEvent,
  TaskCompletionListener,
} from "./use-task-completion-events";
import type { WidgetStateApi } from "./use-widget-state";

export interface SetupGuideContextValue {
  /** Active product the widget is showing. Null when no in-flight setup. */
  activeProduct: Product | null;

  /** Progress row for the active product (null = not yet loaded or none). */
  activeProgress: SetupProgress | null;

  /** All in-flight progress rows for the team, keyed by product. */
  progressByProduct: Partial<Record<Product, SetupProgress>>;

  /** True while the initial server fetch hasn't completed. */
  loading: boolean;

  /** True when the intake modal needs to render (active product, not yet completed). */
  showIntake: boolean;

  /** Widget visibility + close/collapse mutators (per-device + team-dismiss aware). */
  widget: WidgetStateApi;

  // Mutators ------------------------------------------------------------

  /** Submits intake answers + advances to the widget view. */
  submitIntake: (product: Product, answers: IntakeAnswers) => Promise<void>;

  /** Fire-and-forget visit recording. Never throws. */
  recordTaskVisited: (product: Product, taskId: TaskId) => void;

  /** Manual completion for tasks without a derivable predicate. */
  markTaskComplete: (product: Product, taskId: TaskId) => Promise<void>;

  /** Per-task dismiss (optional tasks the merchant doesn't want to do). */
  dismissTask: (product: Product, taskId: TaskId) => Promise<void>;

  /** Team-scoped dismiss — hides widget + header tab for everyone on the team. */
  dismissGuideForTeam: (product: Product) => Promise<void>;

  // Event channel -------------------------------------------------------

  /**
   * Contextual modals call this after their mutation succeeds. Triggers
   * a progress refetch so derived completion flips immediately.
   */
  signalTaskCompletion: (event: TaskCompletionEvent) => void;

  /**
   * Subscribe to task completion attempts (used by component tests +
   * future cross-cutting consumers). Returns unsubscribe.
   */
  subscribeTaskCompletion: (listener: TaskCompletionListener) => () => void;
}

export const SetupGuideContext = createContext<SetupGuideContextValue | null>(
  null,
);
